import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { doc, query, where } from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { useState } from 'react';
import moment from 'moment-timezone';
import { ApiTokenModel, fullScope } from '../models/database/ApiToken';
import { UserModel } from '../models/database/User';
import { useCreateApiToken } from '../methods/createApiToken';
import { useDeleteApiToken } from '../methods/deleteApiToken';
import FirebaseAuth from '../services/FirebaseAuth';
import Loader from '../components/Loader';
import './ApiKeysRoute.css';

interface SnackMsg {
  type: 'success' | 'warning' | 'info' | 'error';
  message: string;
  isShown: boolean;
}

const scopeArr = fullScope;

function ScopeCell({ apiToken }: { apiToken: ApiTokenModel }) {
  const scope = Array.isArray(apiToken.scope) ? apiToken.scope : [];
  if (scope.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const isFull = scope.length === fullScope.length;
  const label = isFull ? 'Full access' : `${scope.length} scope${scope.length === 1 ? '' : 's'}`;
  return (
    <Tooltip
      title={
        <Stack spacing={0.25}>
          {scope.map((s) => (
            <Typography key={s} variant="caption" sx={{ fontFamily: 'monospace' }}>
              {s}
            </Typography>
          ))}
        </Stack>
      }
    >
      <Chip
        label={label}
        size="small"
        color={isFull ? 'primary' : 'default'}
        variant={isFull ? 'filled' : 'outlined'}
      />
    </Tooltip>
  );
}

function getRequestsThisMonth(apiToken: ApiTokenModel): number {
  const dt = moment();
  const yearBucket = apiToken.requests?.[dt.format('YYYY')];
  return yearBucket?.[dt.format('MM')] ?? 0;
}

function ApiKeysRoute() {
  const apiTokensQuery = query(
    ApiTokenModel.parent,
    where('user', '==', doc(UserModel.parent, `${FirebaseAuth.currentUser?.uid}`))
  );
  const [apiTokens] = useCollectionData(apiTokensQuery);
  const [createRun, createLoading] = useCreateApiToken();
  const [deleteRun, deleteLoading] = useDeleteApiToken();

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmRefresh, setConfirmRefresh] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showMsg, setShowMsg] = useState<SnackMsg>({
    type: 'success',
    message: '',
    isShown: false,
  });

  const busy = createLoading || deleteLoading;

  const translateError = (
    err: unknown,
    action: 'create' | 'refresh' | 'delete'
  ): string => {
    if (err instanceof Error && err.message === '#000004') {
      switch (action) {
        case 'create':
          return 'You do not have permission to create a token.';
        case 'refresh':
          return 'You do not have permission to refresh this token.';
        case 'delete':
          return 'You do not have permission to delete this token.';
      }
    }
    return err instanceof Error ? err.message : 'Unknown error';
  };

  const showError = (
    err: unknown,
    action: 'create' | 'refresh' | 'delete'
  ) => {
    setShowMsg({
      type: 'error',
      message: translateError(err, action),
      isShown: true,
    });
  };

  const handleCreate = async () => {
    try {
      await createRun({ scope: scopeArr });
      setShowMsg({ type: 'success', message: 'Created!', isShown: true });
    } catch (err) {
      showError(err, 'create');
    }
  };

  const handleRefresh = async (id: string) => {
    setConfirmRefresh(null);
    setBusyId(id);
    try {
      await createRun({ id, scope: scopeArr });
      setShowMsg({ type: 'success', message: 'Updated!', isShown: true });
    } catch (err) {
      showError(err, 'refresh');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDelete(null);
    setBusyId(id);
    try {
      await deleteRun({ id });
      setShowMsg({ type: 'success', message: 'Removed!', isShown: true });
    } catch (err) {
      showError(err, 'delete');
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setShowMsg({ type: 'success', message: 'Copied!', isShown: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      setShowMsg({ type: 'error', message, isShown: true });
    }
  };

  if (!apiTokens) return <Loader />;

  return (
    <div className="ApiKeysRoute">
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        <AlertTitle>Personal API tokens</AlertTitle>
        <Typography variant="body2">
          Issue a token to access the REST API on behalf of your account.
          Each token has a monthly request quota; refresh it to rotate the
          secret without losing the same token id.
        </Typography>
      </Alert>

      {apiTokens.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No API tokens yet.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                createLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddCircleOutlineOutlinedIcon />
                )
              }
              onClick={handleCreate}
              disabled={busy}
            >
              Create token
            </Button>
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2">Active tokens</Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                createLoading && !busyId ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddCircleOutlineOutlinedIcon />
                )
              }
              onClick={handleCreate}
              disabled={busy}
            >
              New token
            </Button>
          </Toolbar>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="api tokens">
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">Scope</TableCell>
                <TableCell align="center">Requests (month)</TableCell>
                <TableCell align="center">Last use</TableCell>
                <TableCell align="center">Copy</TableCell>
                <TableCell align="center">Refresh</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiTokens.map((t) => {
                const requests = getRequestsThisMonth(t);
                const lastUse = moment.isMoment(t.lastRequest)
                  ? t.lastRequest.format('YYYY-MM-DD')
                  : '—';
                const rowBusy = busyId === t.id;
                return (
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    key={`tokens_table_row_${t.id}`}
                  >
                    <TableCell align="center" sx={{ fontFamily: 'monospace' }}>
                      {t.id}
                    </TableCell>
                    <TableCell align="center">
                      <ScopeCell apiToken={t} />
                    </TableCell>
                    <TableCell align="center">
                      {requests}/{t.limit || 0}
                    </TableCell>
                    <TableCell align="center">{lastUse}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Copy token">
                        <span>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={busy || !t.token}
                            onClick={() => handleCopy(t.token || '')}
                          >
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Refresh token">
                        <span>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              t.id && setConfirmRefresh(t.id as string)
                            }
                          >
                            {rowBusy && createLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <UpdateOutlinedIcon fontSize="small" />
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete token">
                        <span>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              t.id && setConfirmDelete(t.id as string)
                            }
                          >
                            {rowBusy && deleteLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={showMsg.isShown}
        autoHideDuration={6000}
        onClose={() => setShowMsg({ ...showMsg, isShown: false })}
        sx={{ width: '100%', padding: '3vmin', justifyContent: 'end' }}
      >
        <Alert
          onClose={() => setShowMsg({ ...showMsg, isShown: false })}
          sx={{ marginRight: '3vmin' }}
          severity={showMsg.type}
        >
          {showMsg.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmRefresh !== null}
        onClose={() => setConfirmRefresh(null)}
      >
        <DialogTitle>Refresh API token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current secret will be rotated. Any client still using the old
            secret will start receiving authentication errors until you copy
            the new value.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRefresh(null)}>Cancel</Button>
          <Button
            color="primary"
            onClick={() => confirmRefresh && handleRefresh(confirmRefresh)}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Delete API token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This token will be revoked immediately. Any client using it will
            stop working — you'll need to issue a new token to regain access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => confirmDelete && handleDelete(confirmDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ApiKeysRoute;
